import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { customerService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import {
	MagnifyingGlassIcon,
	ArrowPathIcon,
	EyeIcon,
	PencilIcon,
	ChartBarIcon,
	UserIcon,
	CurrencyDollarIcon,
	CalendarIcon,
	ShoppingBagIcon,
	PlusIcon,
	TrashIcon
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';

const Customers = () => {
	const { hasRole } = useAuth();
	const [customers, setCustomers] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [searchTerm, setSearchTerm] = useState('');
	const [customerTypeFilter, setCustomerTypeFilter] = useState('');
	const [sortConfig, setSortConfig] = useState({
		                                             key: 'created_at',
		                                             direction: 'desc'
	                                             });
	const [expandedRows, setExpandedRows] = useState([]);

	// Pagination state
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);

	const fetchCustomers = async (pageNumber = 1) => {
		try {
			setLoading(true);
			const params = {
				page: pageNumber,
				sort_by: sortConfig.key,
				sort_order: sortConfig.direction,
				per_page: 15
			};

			if (searchTerm && searchTerm.length >= 2) {
				params.search = searchTerm;
			}

			if (customerTypeFilter) {
				params.customer_type = customerTypeFilter;
			}

			const response = await customerService.getCustomers(params);

			setCustomers(response.data?.data || []);
			setTotalPages(response.data?.last_page || 1);
			setCurrentPage(response.data?.current_page || 1);
			setError(null);
		} catch (err) {
			console.error('Error fetching customers:', err);
			setError('Failed to load customers. Please try again later.');
			toast.error('Failed to load customers');
		} finally {
			setLoading(false);
		}
	};

	// Fetch customers when filters change
	useEffect(() => {
		if (searchTerm === '' || searchTerm.length >= 2) {
			fetchCustomers(1);
		}
	}, [sortConfig, searchTerm, customerTypeFilter]);

	// Sort customers
	const requestSort = (key) => {
		let direction = 'asc';
		if (sortConfig.key === key && sortConfig.direction === 'asc') {
			direction = 'desc';
		}
		setSortConfig({ key, direction });
	};

	// Handle page change
	const handlePageChange = (pageNumber) => {
		if (pageNumber >= 1 && pageNumber <= totalPages) {
			fetchCustomers(pageNumber);
		}
	};

	// Toggle row expansion
	const toggleRowExpansion = (customerId) => {
		setExpandedRows(prevExpandedRows =>
			                prevExpandedRows.includes(customerId)
			                ? prevExpandedRows.filter(id => id !== customerId)
			                : [...prevExpandedRows, customerId]
		);
	};

	// Delete customer
	const handleDeleteCustomer = async (customer) => {
		if (!window.confirm(`Are you sure you want to delete/deactivate ${customer.customer_name}?`)) {
			return;
		}

		try {
			await customerService.deleteCustomer(customer.id);
			toast.success('Customer deleted/deactivated successfully');
			fetchCustomers(currentPage);
		} catch (error) {
			console.error('Error deleting customer:', error);
			toast.error(error.response?.data?.message || 'Failed to delete customer');
		}
	};

	// Get customer segment badge color
	const getSegmentBadgeColor = (segment) => {
		switch (segment?.toLowerCase()) {
			case 'vip customer':
				return 'bg-purple-100 text-purple-800';
			case 'loyal customer':
				return 'bg-green-100 text-green-800';
			case 'regular customer':
				return 'bg-blue-100 text-blue-800';
			case 'at risk customer':
				return 'bg-yellow-100 text-yellow-800';
			case 'lost customer':
				return 'bg-red-100 text-red-800';
			case 'new customer':
				return 'bg-gray-100 text-gray-800';
			default:
				return 'bg-gray-100 text-gray-800';
		}
	};

	// Format currency
	const formatCurrency = (amount) => {
		return new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: 'USD'
		}).format(amount || 0);
	};

	// Format date
	const formatDate = (dateString) => {
		if (!dateString) return 'N/A';
		return format(new Date(dateString), 'MMM d, yyyy');
	};

	if (loading) {
		return (
			<div className="flex items-center justify-center h-full">
				<div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded shadow-md">
				<p className="font-bold">Error</p>
				<p>{error}</p>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div className="flex justify-between items-center">
				<h1 className="text-2xl font-semibold text-gray-800">Customers</h1>
				{hasRole('admin') && (
					<Link
						to="/customers/new"
						className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
					>
						<PlusIcon className="h-5 w-5 mr-2" /> New Customer
					</Link>
				)}
			</div>

			{/* Search and filters */}
			<div className="bg-white p-4 rounded-lg shadow">
				<div className="flex flex-col md:flex-row items-center space-y-4 md:space-y-0 md:space-x-4">
					<div className="w-full md:w-1/2">
						<label htmlFor="search" className="sr-only">
							Search
						</label>
						<div className="relative">
							<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
								<MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
							</div>
							<input
								id="search"
								name="search"
								type="text"
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
								placeholder="Search customers by name, email, or username"
							/>
						</div>
					</div>

					<div className="w-full md:w-1/4">
						<label htmlFor="customerTypeFilter" className="sr-only">
							Customer Type
						</label>
						<select
							id="customerTypeFilter"
							name="customerTypeFilter"
							value={customerTypeFilter}
							onChange={(e) => setCustomerTypeFilter(e.target.value)}
							className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
						>
							<option value="">All Types</option>
							<option value="retail">Retail</option>
							<option value="wholesale">Wholesale</option>
						</select>
					</div>

					<div className="w-full md:w-auto">
						<button
							onClick={() => {
								setSearchTerm('');
								setCustomerTypeFilter('');
								setSortConfig({
									              key: 'created_at',
									              direction: 'desc'
								              });
								fetchCustomers(1);
							}}
							className="w-full md:w-auto inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
						>
							<ArrowPathIcon className="h-5 w-5 mr-2" /> Reset Filters
						</button>
					</div>
				</div>
			</div>

			{/* Desktop Table */}
			<div className="hidden md:block bg-white shadow overflow-hidden sm:rounded-md">
				<table className="min-w-full divide-y divide-gray-200">
					<thead className="bg-gray-50">
					<tr>
						<th
							scope="col"
							className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
							onClick={() => requestSort('customer_name')}
						>
							<div className="flex items-center space-x-1">
								<span>Customer</span>
								{sortConfig.key === 'customer_name' && (
									<span className="text-primary-500">
                                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                        </span>
								)}
							</div>
						</th>
						<th
							scope="col"
							className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
							onClick={() => requestSort('total_orders')}
						>
							<div className="flex items-center space-x-1">
								<span>Orders</span>
								{sortConfig.key === 'total_orders' && (
									<span className="text-primary-500">
                                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                        </span>
								)}
							</div>
						</th>
						<th
							scope="col"
							className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
							onClick={() => requestSort('total_spent')}
						>
							<div className="flex items-center space-x-1">
								<span>Total Spent</span>
								{sortConfig.key === 'total_spent' && (
									<span className="text-primary-500">
                                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                        </span>
								)}
							</div>
						</th>
						<th
							scope="col"
							className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
							onClick={() => requestSort('last_order_date')}
						>
							<div className="flex items-center space-x-1">
								<span>Last Order</span>
								{sortConfig.key === 'last_order_date' && (
									<span className="text-primary-500">
                                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                        </span>
								)}
							</div>
						</th>
						<th
							scope="col"
							className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
							onClick={() => requestSort('first_order_date')}
						>
							<div className="flex items-center space-x-1">
								<span>First Order</span>
								{sortConfig.key === 'first_order_date' && (
									<span className="text-primary-500">
                                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                        </span>
								)}
							</div>
						</th>
						<th scope="col" className="relative px-6 py-3">
							<span className="sr-only">Actions</span>
						</th>
					</tr>
					</thead>
					<tbody className="bg-white divide-y divide-gray-200">
					{customers.map((customer) => (
						<React.Fragment key={customer.id}>
							<tr className="hover:bg-gray-50">
								<td className="px-6 py-4 whitespace-nowrap">
									<div className="flex items-center">
										<div className="flex-shrink-0 h-10 w-10">
											<div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
												<UserIcon className="h-6 w-6 text-gray-600" />
											</div>
										</div>
										<div className="ml-4">
											<div className="text-sm font-medium text-gray-900">
												{customer.customer_name || 'N/A'}
											</div>
											<div className="text-sm text-gray-500">
												{customer.customer_email}
											</div>
											{customer.customer_username && (
												<div className="text-xs text-gray-400">
													@{customer.customer_username}
												</div>
											)}
											<div className="text-xs">
                                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
	                                                    customer.customer_type === 'wholesale'
	                                                    ? 'bg-purple-100 text-purple-800'
	                                                    : 'bg-blue-100 text-blue-800'
                                                    }`}>
                                                        {customer.customer_type}
                                                    </span>
											</div>
										</div>
									</div>
								</td>
								<td className="px-6 py-4 whitespace-nowrap">
									<div className="flex items-center">
										<ShoppingBagIcon className="h-4 w-4 text-gray-400 mr-1" />
										<span className="text-sm text-gray-900">{customer.total_orders}</span>
									</div>
								</td>
								<td className="px-6 py-4 whitespace-nowrap">
									<div className="flex items-center">
										<CurrencyDollarIcon className="h-4 w-4 text-gray-400 mr-1" />
										<span className="text-sm text-gray-900">
                                                {formatCurrency(customer.total_spent)}
                                            </span>
									</div>
								</td>
								<td className="px-6 py-4 whitespace-nowrap">
									<div className="flex items-center">
										<CalendarIcon className="h-4 w-4 text-gray-400 mr-1" />
										<span className="text-sm text-gray-900">
                                                {formatDate(customer.last_order_date)}
                                            </span>
									</div>
								</td>
								<td className="px-6 py-4 whitespace-nowrap">
									<div className="flex items-center">
										<CalendarIcon className="h-4 w-4 text-gray-400 mr-1" />
										<span className="text-sm text-gray-900">
                                                {formatDate(customer.first_order_date)}
                                            </span>
									</div>
								</td>
								<td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
									<div className="flex items-center space-x-2">
										<button
											onClick={() => toggleRowExpansion(customer.id)}
											className="text-primary-600 hover:text-primary-900"
											title="View Details"
										>
											<EyeIcon className="h-4 w-4" />
										</button>
										<Link
											to={`/customers/${customer.id}/analytics`}
											className="text-blue-600 hover:text-blue-900"
											title="View Analytics"
										>
											<ChartBarIcon className="h-4 w-4" />
										</Link>
										<Link
											to={`/customers/${customer.id}/orders`}
											className="text-green-600 hover:text-green-900"
											title="View Orders"
										>
											<ShoppingBagIcon className="h-4 w-4" />
										</Link>
										{hasRole('admin') && (
											<>
												<Link
													to={`/customers/${customer.id}/edit`}
													className="text-indigo-600 hover:text-indigo-900"
													title="Edit Customer"
												>
													<PencilIcon className="h-4 w-4" />
												</Link>
												<button
													onClick={() => handleDeleteCustomer(customer)}
													className="text-red-600 hover:text-red-900"
													title="Delete Customer"
												>
													<TrashIcon className="h-4 w-4" />
												</button>
											</>
										)}
									</div>
								</td>
							</tr>
							{expandedRows.includes(customer.id) && (
								<tr>
									<td colSpan="6" className="px-6 py-4 bg-gray-50">
										<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
											<div>
												<h4 className="text-sm font-medium text-gray-900 mb-2">Customer Info</h4>
												<div className="text-sm text-gray-600">
													<p><strong>ID:</strong> {customer.id}</p>
													<p><strong>Email:</strong> {customer.customer_email}</p>
													<p><strong>Type:</strong> {customer.customer_type}</p>
													{customer.customer_username && (
														<p><strong>Username:</strong> {customer.customer_username}</p>
													)}
													{customer.phone && (
														<p><strong>Phone:</strong> {customer.phone}</p>
													)}
												</div>
											</div>
											<div>
												<h4 className="text-sm font-medium text-gray-900 mb-2">Order Stats</h4>
												<div className="text-sm text-gray-600">
													<p><strong>Total Orders:</strong> {customer.total_orders}</p>
													<p><strong>Total Spent:</strong> {formatCurrency(customer.total_spent)}</p>
													<p><strong>Avg Order:</strong> {formatCurrency(customer.total_spent / customer.total_orders)}</p>
												</div>
											</div>
											<div>
												<h4 className="text-sm font-medium text-gray-900 mb-2">Quick Actions</h4>
												<div className="flex flex-wrap gap-2">
													<Link
														to={`/customers/${customer.id}/orders`}
														className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-white bg-primary-600 hover:bg-primary-700"
													>
														View Orders
													</Link>
													<Link
														to={`/customers/${customer.id}/analytics`}
														className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
													>
														Analytics
													</Link>
													{hasRole('admin') && (
														<Link
															to={`/customers/${customer.id}/edit`}
															className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
														>
															Edit
														</Link>
													)}
												</div>
											</div>
										</div>
									</td>
								</tr>
							)}
						</React.Fragment>
					))}
					</tbody>
				</table>
			</div>

			{/* Mobile List */}
			<div className="md:hidden space-y-4">
				{customers.map((customer) => (
					<div key={customer.id} className="bg-white shadow rounded-lg p-4">
						<div className="flex items-center justify-between mb-3">
							<div className="flex items-center">
								<div className="flex-shrink-0 h-10 w-10">
									<div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
										<UserIcon className="h-6 w-6 text-gray-600" />
									</div>
								</div>
								<div className="ml-3">
									<p className="text-sm font-medium text-gray-900">
										{customer.customer_name || 'N/A'}
									</p>
									<p className="text-sm text-gray-500">{customer.customer_email}</p>
									<div className="mt-1">
                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
	                                        customer.customer_type === 'wholesale'
	                                        ? 'bg-purple-100 text-purple-800'
	                                        : 'bg-blue-100 text-blue-800'
                                        }`}>
                                            {customer.customer_type}
                                        </span>
									</div>
								</div>
							</div>
							<button
								onClick={() => toggleRowExpansion(customer.id)}
								className="text-primary-600 hover:text-primary-900"
							>
								<EyeIcon className="h-5 w-5" />
							</button>
						</div>

						<div className="grid grid-cols-2 gap-4 text-sm">
							<div>
								<p className="text-gray-500">Orders</p>
								<p className="font-medium">{customer.total_orders}</p>
							</div>
							<div>
								<p className="text-gray-500">Total Spent</p>
								<p className="font-medium">{formatCurrency(customer.total_spent)}</p>
							</div>
							<div>
								<p className="text-gray-500">Last Order</p>
								<p className="font-medium">{formatDate(customer.last_order_date)}</p>
							</div>
							<div>
								<p className="text-gray-500">First Order</p>
								<p className="font-medium">{formatDate(customer.first_order_date)}</p>
							</div>
						</div>

						{expandedRows.includes(customer.id) && (
							<div className="mt-4 pt-4 border-t border-gray-200">
								<div className="flex flex-wrap gap-2">
									<Link
										to={`/customers/${customer.id}/orders`}
										className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
									>
										View Orders
									</Link>
									<Link
										to={`/customers/${customer.id}/analytics`}
										className="inline-flex items-center px-3 py-1 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
									>
										Analytics
									</Link>
									{hasRole('admin') && (
										<>
											<Link
												to={`/customers/${customer.id}/edit`}
												className="inline-flex items-center px-3 py-1 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
											>
												Edit
											</Link>
											<button
												onClick={() => handleDeleteCustomer(customer)}
												className="inline-flex items-center px-3 py-1 border border-red-300 text-xs font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
											>
												Delete
											</button>
										</>
									)}
								</div>
							</div>
						)}
					</div>
				))}
			</div>

			{/* Pagination */}
			{customers.length > 0 && (
				<div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
					<div className="flex-1 flex justify-between sm:hidden">
						<button
							onClick={() => handlePageChange(currentPage - 1)}
							disabled={currentPage <= 1}
							className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
						>
							Previous
						</button>
						<button
							onClick={() => handlePageChange(currentPage + 1)}
							disabled={currentPage >= totalPages}
							className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
						>
							Next
						</button>
					</div>
					<div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
						<div>
							<p className="text-sm text-gray-700">
								Page <span className="font-medium">{currentPage}</span> of{' '}
								<span className="font-medium">{totalPages}</span>
							</p>
						</div>
						<div>
							<nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
								<button
									onClick={() => handlePageChange(currentPage - 1)}
									disabled={currentPage <= 1}
									className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
								>
									Previous
								</button>
								{Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
									const pageNum = currentPage - 2 + i;
									if (pageNum < 1 || pageNum > totalPages) return null;
									return (
										<button
											key={pageNum}
											onClick={() => handlePageChange(pageNum)}
											className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
												pageNum === currentPage
												? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
												: 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
											}`}
										>
											{pageNum}
										</button>
									);
								})}
								<button
									onClick={() => handlePageChange(currentPage + 1)}
									disabled={currentPage >= totalPages}
									className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
								>
									Next
								</button>
							</nav>
						</div>
					</div>
				</div>
			)}

			{customers.length === 0 && !loading && (
				<div className="text-center py-12">
					<UserIcon className="mx-auto h-12 w-12 text-gray-400" />
					<h3 className="mt-2 text-sm font-medium text-gray-900">No customers found</h3>
					<p className="mt-1 text-sm text-gray-500">
						{searchTerm ? 'Try adjusting your search criteria.' : 'No customers have been created yet.'}
					</p>
				</div>
			)}
		</div>
	);
};

export default Customers;
